// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Box from "../../resources/layout-and-structure/Box.res.js";
import * as Env from "../../core/Env.res.js";
import * as I18n from "../../primitives/I18n.res.js";
import * as Json from "../../primitives/Json.res.js";
import * as Page from "../../resources/layout-and-structure/Page.res.js";
import * as Badge from "../../resources/feedback-indicators/Badge.res.js";
import * as Stack from "../../resources/layout-and-structure/Stack.res.js";
import * as React from "react";
import * as Button from "../../resources/actions/Button.res.js";
import * as Future from "rescript-future/src/Future.res.js";
import * as Request from "../../core/Request.res.js";
import * as SearchBar from "../../resources/selection-and-input/SearchBar.res.js";
import * as TableView from "../../resources/tables/TableView.res.js";
import * as TextStyle from "../../resources/typography/TextStyle.res.js";
import * as BarControl from "../../resources/layout-and-structure/BarControl.res.js";
import * as EmptyState from "../../resources/layout-and-structure/EmptyState.res.js";
import * as AsyncResult from "../../primitives/AsyncResult.res.js";
import * as Auth__Types from "../../bundles/Auth/Auth__Types.res.js";
import * as Stdlib_Math from "@rescript/runtime/lib/es6/Stdlib_Math.js";
import * as Illustration from "../../resources/images-and-icons/Illustration.res.js";
import * as KbdClipboard from "../../resources/typography/KbdClipboard.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as LegacyPagination from "../../resources/navigation/LegacyPagination.res.js";
import * as JsxRuntime from "react/jsx-runtime";

function decodeResultItem(json) {
  let dict = Json.decodeDict(json);
  let match = Json.flatDecodeDictFieldString(dict, "id");
  let match$1 = Json.flatDecodeDictFieldString(dict, "name");
  let match$2 = Json.flatDecodeDictFieldString(dict, "corporateName");
  let match$3 = Json.flatDecodeDictFieldString(dict, "legalRepresentative");
  let match$4 = Json.flatDecodeDictFieldString(dict, "email");
  let match$5 = Json.flatDecodeDictFieldString(dict, "type");
  if (match !== undefined && match$1 !== undefined && match$2 !== undefined && match$3 !== undefined && match$4 !== undefined) {
    return {
      id: match,
      name: match$1,
      corporateName: match$2,
      legalRepresentative: match$3,
      email: match$4,
      type_: match$5
    };
  }
}

function decodeResult(json) {
  let resultItems = Json.decodeArray(json);
  if (resultItems !== undefined) {
    return Stdlib_Array.filterMap(resultItems, decodeResultItem);
  } else {
    return [];
  }
}

let endpoint = Env.gatewayUrl() + "/admin/shops";

function make() {
  return Future.mapOk(Request.make(endpoint, "GET", undefined, undefined, undefined), undefined, decodeResult);
}

let QueryAllShopsRequest = {
  decodeResultItem: decodeResultItem,
  decodeResult: decodeResult,
  endpoint: endpoint,
  make: make
};

function keyExtractor(row) {
  return row.id;
}

function sanitize(string) {
  return string.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9 ]/g, "");
}

function match(row, query) {
  if (query === "" || row.id === query || sanitize(row.name).includes(sanitize(query)) || sanitize(row.corporateName).includes(sanitize(query)) || sanitize(row.legalRepresentative).includes(sanitize(query))) {
    return true;
  } else {
    return sanitize(row.email).includes(sanitize(query));
  }
}

function fromQueryItem(queryItem) {
  return {
    id: queryItem.id,
    name: queryItem.name,
    corporateName: queryItem.corporateName,
    legalRepresentative: queryItem.legalRepresentative,
    email: queryItem.email,
    kind: Stdlib_Option.getOr(Stdlib_Option.flatMap(queryItem.type_, Auth__Types.ShopKind.fromString), "INDEPENDENT")
  };
}

let TableRow = {
  keyExtractor: keyExtractor,
  sanitize: sanitize,
  match: match,
  fromQueryItem: fromQueryItem
};

function totalPages(rows) {
  return Stdlib_Math.Int.ceil(rows.length / 10);
}

function search(rows, searchQuery) {
  return rows.filter(row => match(row, searchQuery));
}

function paginate(rows, currentPage) {
  return rows.slice((currentPage - 1 | 0) * 10 | 0, currentPage * 10 | 0);
}

function fromQueryAllShops(queryAllShops) {
  return queryAllShops.map(fromQueryItem);
}

let TableRows = {
  rowsPerPage: 10,
  totalPages: totalPages,
  search: search,
  paginate: paginate,
  fromQueryAllShops: fromQueryAllShops
};

let tableColumns = [
  {
    key: "name",
    name: I18n.t("Name"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 260
      },
      width: {
        NAME: "fr",
        VAL: 1.5
      }
    },
    render: param => {
      let match = param.data;
      return JsxRuntime.jsx(Box.make, {
        children: JsxRuntime.jsxs(Stack.make, {
          children: [
            JsxRuntime.jsx(TextStyle.make, {
              children: match.name,
              weight: "strong"
            }),
            JsxRuntime.jsx(TextStyle.make, {
              children: match.corporateName,
              variation: "normal",
              size: "xxsmall"
            })
          ],
          space: "xxsmall"
        }),
        spaceY: "xxsmall"
      });
    }
  },
  {
    key: "legal-representative",
    name: I18n.t("Legal representative"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 160
      }
    },
    render: param => JsxRuntime.jsx(TextStyle.make, {
      children: param.data.legalRepresentative
    })
  },
  {
    key: "email",
    name: I18n.t("Email address"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 320
      },
      margin: "large"
    },
    render: param => JsxRuntime.jsx(TextStyle.make, {
      children: param.data.email
    })
  },
  {
    key: "kind",
    name: I18n.t("Kind"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 160
      }
    },
    render: param => {
      let kind = param.data.kind;
      return JsxRuntime.jsx(Badge.make, {
        children: Auth__Types.ShopKind.toLabel(kind),
        variation: kind === "WAREHOUSE" ? "neutral" : (
            kind === "INTEGRATED" ? "primary" : (
                kind === "INDEPENDENT" ? "information" : (
                    kind === "AFFILIATED" ? "important" : "warning"
                  )
              )
          )
      });
    }
  },
  {
    key: "action",
    name: I18n.t("Copy ID"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 160
      },
      alignX: "flexEnd"
    },
    render: param => JsxRuntime.jsx(KbdClipboard.make, {
      cropValueAt: 10,
      value: param.data.id
    })
  }
];

let initialState_asyncResult = AsyncResult.notAsked();

let initialState = {
  searchQuery: "",
  currentPage: 1,
  asyncResult: initialState_asyncResult
};

function make$1(prevState, action) {
  switch (action.TAG) {
    case "SearchQueryChanged" :
      return {
        searchQuery: action._0,
        currentPage: 1,
        asyncResult: prevState.asyncResult
      };
    case "AsyncResultGet" :
      return {
        searchQuery: prevState.searchQuery,
        currentPage: 1,
        asyncResult: action._0
      };
    case "Paginated" :
      let totalPages = action._1;
      let match = prevState.currentPage;
      let tmp;
      switch (action._0) {
        case "First" :
          tmp = 1;
          break;
        case "Prev" :
          tmp = match !== 1 ? prevState.currentPage - 1 | 0 : 1;
          break;
        case "Next" :
          tmp = match >= totalPages ? match : prevState.currentPage + 1 | 0;
          break;
        case "Last" :
          tmp = totalPages;
          break;
      }
      return {
        searchQuery: prevState.searchQuery,
        currentPage: tmp,
        asyncResult: prevState.asyncResult
      };
  }
}

let Reducer = {
  initialState: initialState,
  make: make$1
};

function AdminShopListPage(props) {
  let mounted = React.useRef(false);
  let match = React.useReducer(make$1, initialState);
  let state = match[0];
  let currentPage = state.currentPage;
  let searchQuery = state.searchQuery;
  let dispatch = match[1];
  React.useEffect(() => {
    if (mounted.current) {
      return;
    }
    let request = Future.mapError(make(), undefined, param => {});
    dispatch({
      TAG: "AsyncResultGet",
      _0: AsyncResult.loading()
    });
    Future.get(request, result => dispatch({
      TAG: "AsyncResultGet",
      _0: AsyncResult.done(result)
    }));
    return () => Future.cancel(request);
  }, []);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  let tableRows = AsyncResult.mapOk(state.asyncResult, fromQueryAllShops);
  let searchedTableRows = AsyncResult.mapOk(tableRows, rows => search(rows, searchQuery));
  let totalPages$1;
  if (typeof searchedTableRows !== "object") {
    totalPages$1 = 1;
  } else if (searchedTableRows.TAG === "Reloading") {
    let rows = searchedTableRows._0;
    totalPages$1 = rows.TAG === "Ok" ? totalPages(rows._0) : 1;
  } else {
    let rows$1 = searchedTableRows._0;
    totalPages$1 = rows$1.TAG === "Ok" ? totalPages(rows$1._0) : 1;
  }
  let searchedAndPaginatedTableRows = AsyncResult.mapOk(searchedTableRows, rows => paginate(rows, currentPage));
  let onRequestSearch = queryString => dispatch({
    TAG: "SearchQueryChanged",
    _0: queryString
  });
  let onRequestPaginate = paginateAction => dispatch({
    TAG: "Paginated",
    _0: paginateAction,
    _1: totalPages$1
  });
  let match$1 = state.asyncResult;
  let placeholderEmptyState;
  if (typeof searchedAndPaginatedTableRows !== "object" || searchedAndPaginatedTableRows.TAG !== "Done") {
    placeholderEmptyState = EmptyState.error;
  } else {
    let match$2 = searchedAndPaginatedTableRows._0;
    if (match$2.TAG === "Ok" && match$2._0.length === 0 && typeof match$1 === "object" && match$1.TAG === "Done") {
      let rows$2 = match$1._0;
      placeholderEmptyState = rows$2.TAG === "Ok" && rows$2._0.length !== 0 ? JsxRuntime.jsx(EmptyState.make, {
          illustration: Illustration.noResultFound,
          title: I18n.t("No result were found."),
          text: I18n.t("Try again with another keyword or:"),
          children: JsxRuntime.jsx(Button.make, {
            label: I18n.t("Clear search query"),
            onPress: () => dispatch({
              TAG: "SearchQueryChanged",
              _0: ""
            })
          })
        }) : EmptyState.error;
    } else {
      placeholderEmptyState = EmptyState.error;
    }
  }
  let searchBar = JsxRuntime.jsx(Box.make, {
    children: JsxRuntime.jsx(SearchBar.make, {
      placeholder: I18n.t("Search a shop"),
      value: searchQuery,
      onChange: onRequestSearch
    }),
    spaceX: "large",
    spaceBottom: "xmedium"
  });
  return JsxRuntime.jsxs(Page.make, {
    children: [
      JsxRuntime.jsx(BarControl.make, {}),
      JsxRuntime.jsx(TableView.make, {
        data: searchedAndPaginatedTableRows,
        columns: tableColumns,
        keyExtractor: keyExtractor,
        placeholderEmptyState: placeholderEmptyState,
        searchBar: searchBar
      }),
      JsxRuntime.jsx(LegacyPagination.make, {
        currentPage: currentPage,
        totalPages: totalPages$1,
        onRequestPaginate: onRequestPaginate
      })
    ],
    variation: "compact",
    title: I18n.t("Shops"),
    subtitle: I18n.t("List of shops"),
    renderTitleEnd: () => JsxRuntime.jsx(Badge.make, {
      children: I18n.t("Support"),
      variation: "primary"
    })
  });
}

let make$2 = React.memo(AdminShopListPage);

export {
  QueryAllShopsRequest,
  TableRow,
  TableRows,
  tableColumns,
  Reducer,
  make$2 as make,
}
/* endpoint Not a pure module */
